import { ref, computed } from 'vue'
import sha256 from 'crypto-js/sha256'


/**
	* Function: hashString
	* @description This trims, converts the string to lowercase and then hashes it using SHA-256.
	*
	* @param {String} string
*/
export const hashString = string => {
	if (! string) return

	const trimmed = string.trim().toLowerCase() // Trim and convert the string to lowercase
	const result = sha256(trimmed).toString() // Hash the string using SHA-256
	return result
	return `https://gravatar.com/avatar/${result}`
}

/**
 * limitCharacters
 * Limits the input string to a maximum number of characters and appends '...' if the string is truncated.
 *
 * @param {string} string The input string to be limited.
 * @param {number} maxCharacters The maximum number of characters allowed.
 * @return {string} The limited string.
 */
export const limitCharacters = (string, maxCharacters) => {
   if (string.length <= maxCharacters) {
     return string
   } else {
     return string.slice(0, maxCharacters) + '...'
   }
 }

/**
 * limitCharacters
 * Splits the string up in the middle and adds '...' in between.
 *
 * @param {string} string The input string to be limited.
 * @param {number} maxCharacters The maximum number of characters allowed.
 * @return {string} The split string.
 */
export const splitCharacters = (string = '', maxCharacters) => {
	if(! string?.length) return

   if (string.length <= maxCharacters) {
     return string
   } else {
     return string.slice(0, maxCharacters) + '...' + string.slice(string.length - maxCharacters, string.length)
   }
 }

 /**
	* capitalize
	* Capitalize the first letter
	*
	* @param string string
	* @return string
	*
*/
export const capitalize = ( string ) => string.charAt(0).toUpperCase() + string.slice(1)

/**
	* formatUrl
	* Check to see if the url has http or https
	*
	* @param string string
	* @return string
	*
*/
export const formatUrl = (string = '') => string.indexOf("http") < 0 ? 'https://' + string : string

/**
	* arrayStringToNumber
	* Convert an array of strings to integers
	*
	* @param Array stringArray
	* @return Array intArray
	*
*/
export const arrayStringToNumbers = ( stringArray ) => {
  const intArray = []
  stringArray.forEach(value => {
    intArray.push(Number(value))
  })
  return intArray
 }

 /**
  * generateID
  * Generates a random string
  * @param String length (The length of the string in characters)
  * @param String prefix (A prefix before the generated string)
  * @return String
*/
export const generateID = (length, prefix, $type = null) => {
	let result = prefix ?? ''
	let characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789'

	if($type == 'uppercase') {
		characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
	}

	for ( let i = 0; i < length; i++ ) {
		result += characters.charAt(Math.floor(Math.random() * characters.length))
 	}
 return result
}

/**
  * generateNumericID
  * Generates a random nurmeric string
  * @param String length (The length of the string in characters)
  * @param String prefix (A prefix before the generated string)
  * @return String
*/
export const generateNumericID = (length, prefix) => {
	let result = prefix ?? ''
	let characters = '0123456789'
	for ( let i = 0; i < length; i++ ) {
		result += characters.charAt(Math.floor(Math.random() * characters.length))
 	}
 return parseInt(result)
}

	/**
	* slugify
	* Returns a slugified string
	*
	* @param string string
	* @return string
	*
*/
export const slugify = string =>
string
	.toString()
	.normalize('NFD')
	.replace(/[\u0300-\u036f]/g, '')
	.toLowerCase()
	.trim()
	.replace(/\s+/g, '-')
	.replace(/[^\w-]+/g, '')
	.replace(/--+/g, '-')

	/**
		* truncate
		* Returns a truncated string with set length
		*
		* @param String text
		* @param String maxLength
		* @return String
		*
	*/
	export const truncate = (text, maxLength) => text.length > maxLength ? `${text.slice(0, maxLength)}...` : text

	/**
	* getLabel
	* It expects an array of object formatted like { label: 'Some label', value: 'some_value' }.
	* It will filter the value and output the label.
	*
	* @param Array array
	* @param String value
	* @return String
	*
*/
export const getLabel = (array, value) => array.filter(item => item.value == value)[0]?.label

	/**
	* formattedPrelineContent
	* Content from a textarea gets formatted from line breaks
	* to paragraphs.
	*
	* @param String content
	* @return Mixed
	*
*/
export const formattedPrelineContent = content => {
	if(content) {
		const lines = content.split('\n')

		let html = ''
		for (let i = 0; i < lines.length; i++) {
			const line = lines[i].trim()
			if (line !== '') {
				html += '<p>' + line + '</p>'
			}
		}

		return html
	}
}

/**
	* renderLinks
	* Use regex to detect plain text links and convert them to anchor tags
	*
	* @param String text
	* @return String
	*
*/
export const renderLinks = text => {
  // Regular expression to match URLs with or without protocol
  var urlRegex = '/(?:(?<!>)\b(https?:\/\/|www\.)[^\s<]+)/g'

  // Regular expression to match email addresses
  var emailRegex = '/([^\s]+@[^\s]+)/g'

  // Replace URLs with anchor tags
  var linkedText = text.replace(urlRegex, function(url) {
    // Add https:// to URLs without protocol
    if (!/^https?:\/\//i.test(url)) {
      url = 'https://' + url
    }
    var displayedUrl = url.replace(/^https?:\/\//i, '')
    return '<a href="' + url + '" target="_blank">' + displayedUrl + '</a>'
  })

  // Replace email addresses with mailto links
  linkedText = linkedText.replace(emailRegex, function(email) {
    return '<a href="mailto:' + email + '">' + email + '</a>'
  })

  return linkedText
}